import React, { useEffect, useState } from "react";
import axios from "../api/baseurl";
import { useNavigate } from "react-router-dom";
import "./Spinner.css";
import "./Loader.css";
import { toast } from "react-toastify";
import DatePicker from "react-datepicker";
import { Pagination } from "react-bootstrap";
import { format, parse } from "date-fns";
import { FiInfo } from "react-icons/fi";
import InvoiceTransactionsHistory from "./invoiceTransactionsHistory";
import "./hm-theme.css";

function CompanyIncomingInvoices({ selectedOrgcode }) {
  const [invoices, setInvoices] = useState([]);
  const [loader, setLoader] = useState(true);
  const [invoiceNumberFilter, setInvoiceNumberFilter] = useState("");
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [searchTerm] = useState("");

  const token = JSON.parse(localStorage.getItem("data"))?.token;
  const navigate = useNavigate();

  useEffect(() => {
    if (searchTerm || selectedCompany || invoiceNumberFilter || selectedDate || statusFilter !== "all") {
      setCurrentPage(0);
    }
  }, [searchTerm, selectedCompany, invoiceNumberFilter, selectedDate, statusFilter]);

  useEffect(() => {
    if (searchTerm || selectedCompany || invoiceNumberFilter || selectedDate || statusFilter !== "all") {
      searchData(currentPage);
    } else {
      getData(currentPage);
    }
  }, [searchTerm, selectedCompany, invoiceNumberFilter, selectedDate, statusFilter, currentPage]);

  const searchData = async (page = 0) => {
    setLoader(true);
    try {
      const searchUrl = `/api/invoiceSearch/searchLabOrCompanyInvoices?page=${page}`;
      const requestData = {
        invoiceNumber: invoiceNumberFilter || null,
        date: selectedDate || null,
        companyId: selectedCompany || null,
        status: statusFilter === "all" ? null : statusFilter,
        invoicesFor: "Company",
        invoiceType: "incoming"
      };

      const response = await axios.post(searchUrl, requestData, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setInvoices(response.data.content || []);
      setCurrentPage(response.data.number || 0);
      setTotalPages(response.data.totalPages || 0);
      setLoader(false);
    } catch (error) {
      setLoader(false);
      handleError(error);
    }
  };

  const getData = async (pageNumber = 0) => {
    setLoader(true);
    try {
      const compRes = await axios.get(`/api/companydetails/companycode=${selectedOrgcode}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSelectedCompany(compRes.data?.id);

      const trans = await axios.get(`/api/invoices/getCompanyIncomingIncoices/companyId=${compRes.data?.id}`,
        {
          params: { page: pageNumber },
          headers: { Authorization: `Bearer ${token}` },
        });

      setInvoices(trans.data.content || []);
      setCurrentPage(trans.data.number || 0);
      setTotalPages(trans.data.totalPages || 0);
      setLoader(false);
    } catch (error) {
      setLoader(false);
      handleError(error);
    }
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);
  const [isInvoiceTransactionModalOpen, setIsInvoiceTransactionModalOpen] = useState(false);

  const handleOpenTransactionHistory = (invoiceId) => {
    setSelectedInvoiceId(invoiceId);
    setIsInvoiceTransactionModalOpen(true);
  };

  const handleCloseInvoiceTransactionModal = () => {
    setIsInvoiceTransactionModalOpen(false);
    setSelectedInvoiceId(null);
    searchData(0);
  };

  const renderPageNumbers = () => {
    const pageNumbers = [];
    const maxPageNumbersToShow = 5;
    let startPage = Math.max(0, currentPage - 2);
    let endPage = Math.min(totalPages - 1, currentPage + 2);

    if (currentPage <= 2) endPage = Math.min(totalPages - 1, maxPageNumbersToShow - 1);
    if (currentPage >= totalPages - 3) startPage = Math.max(0, totalPages - maxPageNumbersToShow);

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(
        <Pagination.Item key={i} active={i === currentPage} onClick={() => handlePageChange(i)}>
          {i + 1}
        </Pagination.Item>
      );
    }
    return pageNumbers;
  };

  const handleViewPdf = async (id) => {
    await axios({
      url: `/api/invoices/download/id=${id}`,
      method: "GET",
      responseType: "blob",
      headers: { Authorization: `Bearer ${token}` },
    }).then((response) => {
      const contentType = response.headers["content-type"];
      let extension = "";

      if (contentType.includes("application/pdf")) {
        extension = "pdf";
      } else if (contentType.includes("image/jpeg")) {
        extension = "jpg";
      } else if (contentType.includes("image/png")) {
        extension = "png";
      } else {
        console.error("Unsupported file type:", contentType);
        return;
      }

      const url = window.URL.createObjectURL(new Blob([response.data], { type: contentType }));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `file.${extension}`);
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    });
  };

  function handleError(error) {
    if (error.response) {
      if (error.response.status === 401) {
        navigate(`/signin`);
        toast.error("Your session has expired. Please login.");
      } else if (error.response.data && error.response.data.message) {
        toast.error(error.response.data.message);
      } else if (error.response.data && error.response.data.code) {
        toast.error(error.response.data.code);
      } else {
        toast.error("An error occurred.");
      }
    } else if (error.message) {
      toast.error(error.message);
    } else {
      toast.error("An error occurred.");
    }
  }

  const handleSearchInvoiceNumber = (event) => {
    setInvoiceNumberFilter(event.target.value.toLowerCase());
  };

  const handleDateChange = (date) => {
    if (date) {
      const formattedDate = format(date, "MM/yyyy");
      setSelectedDate(formattedDate);
    } else {
      setSelectedDate(null);
    }
  };

  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value);
  };

  return (
    <div>
      {loader && <div className="loading"></div>}

      <div className="hm-card">
        {/* Header */}
        <div className="hm-card-header">
          <h3 className="hm-card-title">
            <i className="material-icons">receipt_long</i>
            Company Incoming Invoices
          </h3>
          <span className="hm-badge hm-badge-primary">{invoices.length} Invoices</span>
        </div>

        {/* Filters */}
        <div className="hm-card-body border-bottom bg-light bg-opacity-50 p-3">
          <div className="row g-3">
            <div className="col-md-4">
              <label htmlFor="invoiceNumberFilter" className="form-label fw-semibold small text-secondary">
                Search by Invoice Number
              </label>
              <input
                type="search"
                className="form-control"
                id="invoiceNumberFilter"
                placeholder="Search by Invoice Number..."
                value={invoiceNumberFilter}
                onChange={handleSearchInvoiceNumber}
              />
            </div>
            <div className="col-md-4">
              <label htmlFor="monthYearFilter" className="form-label fw-semibold small text-secondary">
                Filter by Month & Year
              </label>
              <DatePicker
                selected={selectedDate ? parse(selectedDate, "MM/yyyy", new Date()) : null}
                onChange={handleDateChange}
                showMonthYearPicker
                dateFormat="MM/yyyy"
                className="form-select"
                placeholderText="Select Month and Year"
              />
            </div>
            <div className="col-md-4">
              <label htmlFor="statusFilter" className="form-label fw-semibold small text-secondary">
                Filter by Status
              </label>
              <select
                id="statusFilter"
                className="form-select"
                value={statusFilter || "all"}
                onChange={handleStatusFilterChange}
              >
                <option value="all">ALL STATUSES</option>
                <option value="Invoice Generated">INVOICE GENERATED</option>
                <option value="Partial Payment">PARTIAL PAYMENT</option>
                <option value="Paid">PAID</option>
              </select>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="hm-card-body p-0">
          <div style={{ overflowX: "auto" }}>
            <table className="hm-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Period</th>
                  <th>Invoice Number</th>
                  <th>Gross Amount</th>
                  <th>Tax</th>
                  <th>Net Pay</th>
                  <th>File</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {invoices.length === 0 ? (
                  <tr>
                    <td colSpan="10">
                      <div className="hm-empty-state">
                        {/* <i className="material-icons">receipt_off</i> */}
                        <p>{loader ? "Loading invoices..." : "No Data Available"}</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  invoices.map((invoice) => (
                    <tr key={invoice.id}>
                      <td className="fw-semibold">{invoice.date}</td>
                      <td className="small text-muted">{invoice.startdate} to {invoice.enddate}</td>
                      <td><code>{invoice.invoicenumber}</code></td>
                      <td className="fw-semibold">₹ {invoice.grossamount}</td>
                      <td>₹ {invoice.tax}</td>
                      <td className="fw-bold text-dark">₹ {invoice.netpay}</td>
                      <td>
                        {invoice.invoiceFileUploaded === true ? (
                          <button
                            className="btn btn-outline-primary btn-sm rounded-pill px-3"
                            onClick={() => handleViewPdf(invoice.id)}
                          >
                            <i className="material-icons align-middle me-1" style={{ fontSize: "14px" }}>visibility</i>
                            View PDF
                          </button>
                        ) : (
                          <span className="text-muted small">No File</span>
                        )}
                      </td>
                      <td>
                        <div className="d-flex flex-column gap-1">
                          <div className="d-flex align-items-center gap-1">
                            <span className={
                              invoice.invoiceStatus?.status === "Paid" ? "hm-badge hm-badge-success" :
                                invoice.invoiceStatus?.status === "Partial Payment" ? "hm-badge hm-badge-warning" :
                                  "hm-badge hm-badge-info"
                            }>
                              {invoice.invoiceStatus?.status}
                            </span>
                            {invoice.invoiceStatus?.status === "Paid" && (
                              <FiInfo
                                onClick={() => handleOpenTransactionHistory(invoice.id)}
                                className="text-primary cursor-pointer"
                                style={{ cursor: "pointer" }}
                                title="Transaction History"
                              />
                            )}
                          </div>
                          {invoice.invoiceStatus?.status === "Partial Payment" && (
                            <div className="extra-small mt-1">
                              <span className="text-success fw-bold me-2">Paid: ₹{invoice.paidAmount}</span>
                              <span className="text-danger fw-bold">Pending: ₹{invoice.pendingAmount}</span>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="d-flex justify-content-center py-3">
              <Pagination className="hm-pagination">
                <Pagination.First onClick={() => handlePageChange(0)} disabled={currentPage === 0} />
                <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 0} />
                {renderPageNumbers()}
                <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage >= totalPages - 1} />
                <Pagination.Last onClick={() => handlePageChange(totalPages - 1)} disabled={currentPage >= totalPages - 1} />
              </Pagination>
            </div>
          )}
        </div>
      </div>

      {isInvoiceTransactionModalOpen && (
        <InvoiceTransactionsHistory
          isOpen={isInvoiceTransactionModalOpen}
          onRequestClose={handleCloseInvoiceTransactionModal}
          invoiceId={selectedInvoiceId}
        />
      )}
    </div>
  );
}

export default CompanyIncomingInvoices;

